import { useCallback, useEffect, useState } from "react";
import { getFernausleseChannels } from "@/lib/fernausleseApi";
import { fetchInfoScreenEventsConfig } from "@/lib/infoScreenEventsApi";
import { getIntegrationsStatus } from "@/lib/integrationsStatusApi";
import { getTouristImportStats } from "@/lib/touristImportApi";
import {
  buildIntegrationTileStatuses,
  type IntegrationTileStatus,
} from "@/pages/stammdaten/schnittstellen/buildIntegrationTileStatuses";
import type { SchnittstellenIntegrationId } from "@/pages/stammdaten/schnittstellenIntegrations";

export type { IntegrationTileStatus as IntegrationStatusInfo };

export function useIntegrationStatus(integrationId: SchnittstellenIntegrationId | null) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<IntegrationTileStatus | null>(null);

  const load = useCallback(async () => {
    if (!integrationId) {
      setStatus(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const settings = await getIntegrationsStatus();
      const statusMap = buildIntegrationTileStatuses(settings, null, null, null);
      setStatus(statusMap[integrationId]);
      setLoading(false);

      const [eventsConfig, touristImportStats, fernausleseChannels] = await Promise.all([
        integrationId === "events"
          ? fetchInfoScreenEventsConfig().catch(() => null)
          : Promise.resolve(null),
        integrationId === "tourist-import"
          ? getTouristImportStats({ includeReconcile: false, includeDiagnostics: false }).catch(
              () => null,
            )
          : Promise.resolve(null),
        integrationId === "fernauslese"
          ? getFernausleseChannels().catch(() => null)
          : Promise.resolve(null),
      ]);
      const enriched = buildIntegrationTileStatuses(
        settings,
        eventsConfig,
        touristImportStats,
        fernausleseChannels,
      );
      setStatus(enriched[integrationId]);
    } catch {
      setLoading(false);
    }
  }, [integrationId]);

  useEffect(() => {
    void load();
  }, [load]);

  return { loading, status, reload: load };
}
